/**
 * TextBlock Component
 * 
 * Displays custom text content like notes, announcements, or descriptions.
 * Supports different text styles and alignments.
 */

import React, { memo } from 'react';
import { cn } from '@/lib/utils';
import { TextBlockProps } from '@/types/menuBuilder';
import { useTranslation } from 'react-i18next';

interface TextBlockComponentProps {
  props: TextBlockProps;
  isPreview?: boolean;
}

export const TextBlock = memo(function TextBlock({
  props,
  isPreview = false,
}: TextBlockComponentProps) {
  const { t, i18n } = useTranslation();
  const { 
    content, 
    contentAr, 
    alignment, 
    fontSize = 'md', 
    customFontSize,
    textColor,
    backgroundColor,
    padding = 'md',
    fontWeight = 'normal',
  } = props;

  const isRtl = i18n.language === 'ar';
  const displayContent = isRtl && contentAr ? contentAr : content;

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const fontSizeClasses: Record<string, string> = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    custom: '',
  };

  const fontWeightClasses = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  const paddingClasses = {
    none: 'p-0',
    sm: 'p-2',
    md: 'p-4',
    lg: 'p-6',
  };

  // No content - show placeholder
  if (!displayContent) {
    return (
      <div className="p-4 text-center text-muted-foreground bg-muted/30 rounded-lg">
        {!isPreview && t('menuBuilder.enterText', 'Enter text in properties')}
      </div>
    );
  }

  return (
    <div 
      className={cn(
        'rounded-lg',
        paddingClasses[padding],
        alignmentClasses[alignment]
      )}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <div 
        className={cn(
          'whitespace-pre-wrap',
          fontSize !== 'custom' && fontSizeClasses[fontSize],
          fontWeightClasses[fontWeight]
        )}
        style={{ 
          color: textColor || undefined,
          fontSize: fontSize === 'custom' && customFontSize ? `${customFontSize}px` : undefined
        }}
        dangerouslySetInnerHTML={{ __html: displayContent }}
      />
    </div>
  );
});

export default TextBlock;
